/**
 * Platform README — a login-protected, in-app tour of what has been
 * built, how it works, and where the work is going. Placed under the
 * "Platform" capability in the enterprise sidebar.
 *
 * Route:  /platform/readme
 * Access: enterprise login (wrapped in ProtectedRoute + AppShell).
 *
 * Content is deliberately self-contained (no external MD parsing) so it
 * renders instantly and can pull *live* values (`repository_backend`,
 * platform version) from `/api/health`.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  BookOpen, Compass, Layers, Milestone, Server, Rocket, ShieldCheck,
  Wallet, FileCheck, Award, GitBranch, Database, Cpu, Cloud, KeyRound,
  Activity, CheckCircle2, Circle, Loader2, ExternalLink,
} from "lucide-react";

const NAVY = "#0A1F44";
const NAVY_DARK = "#061633";
const GOLD = "#C9A24A";

const TABS = [
  { key: "overview",     label: "Overview",     icon: Compass },
  { key: "architecture", label: "Architecture", icon: Layers },
  { key: "milestones",   label: "Milestones",   icon: Milestone },
  { key: "features",     label: "Features",     icon: BookOpen },
  { key: "ops",          label: "Ops",          icon: Server },
];

export default function PlatformReadmePage() {
  const [tab, setTab] = useState("overview");
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/health");
        if (alive) setHealth(data);
      } catch {
        if (alive) setHealth({ status: "unreachable" });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#F8F9FA" }} data-testid="platform-readme">
      <Hero health={health} loading={loading} />

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
        <TabBar tab={tab} onChange={setTab} />
        <div className="mt-6 pb-16">
          {tab === "overview"     && <OverviewTab health={health} />}
          {tab === "architecture" && <ArchitectureTab />}
          {tab === "milestones"   && <MilestonesTab />}
          {tab === "features"     && <FeaturesTab />}
          {tab === "ops"          && <OpsTab health={health} />}
        </div>
      </div>
    </div>
  );
}

// ─── Hero ───────────────────────────────────────────────────

function Hero({ health, loading }) {
  return (
    <section
      className="relative overflow-hidden text-white"
      style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 65%, #020814 100%)` }}
    >
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full" style={{ background: `${GOLD}18` }} />
      <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full" style={{ background: `${GOLD}0F` }} />
      <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: GOLD }}>
          <BookOpen className="w-3 h-3" />
          Platform README
        </div>
        <h1 className="font-display text-3xl lg:text-5xl font-semibold mt-3 leading-tight max-w-3xl">
          Altara Consumer Intelligence
        </h1>
        <p className="mt-4 text-white/70 max-w-2xl leading-relaxed">
          A multi-tenant, Azure-first Consumer Trust Infrastructure platform. This document is the
          engineering &amp; product truth of what has been built, how it fits together, and where
          it is going next.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3 text-[11px] font-mono">
          <LivePill label="Status" value={health?.status || (loading ? "…" : "unknown")} tone="emerald" loading={loading} />
          <LivePill label="Version" value={health?.version || "—"} loading={loading} />
          <LivePill label="Env" value={health?.env || "—"} loading={loading} />
          <LivePill label="Repo backend" value={health?.repository_backend || "—"} loading={loading} />
        </div>
      </div>
    </section>
  );
}

function LivePill({ label, value, tone = "slate", loading = false }) {
  const toneClass = {
    emerald: "text-emerald-300 border-emerald-300/40 bg-emerald-500/10",
    slate:   "text-white/80 border-white/20 bg-white/10",
  }[tone] || "text-white/80 border-white/20 bg-white/10";
  return (
    <span
      data-testid={`live-pill-${label.toLowerCase().replace(/\s/g, "-")}`}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${toneClass}`}
    >
      <span className="uppercase tracking-widest text-[9px] text-white/50">{label}</span>
      {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <span className="tabular-nums">{value}</span>}
    </span>
  );
}

// ─── Tab bar ────────────────────────────────────────────────

function TabBar({ tab, onChange }) {
  return (
    <nav
      className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5 flex flex-wrap gap-1"
      data-testid="readme-tabs"
    >
      {TABS.map(t => {
        const active = tab === t.key;
        const Icon = t.icon;
        return (
          <button
            key={t.key}
            data-testid={`readme-tab-${t.key}`}
            onClick={() => onChange(t.key)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-[13px] font-semibold transition"
            style={{
              background: active ? NAVY : "transparent",
              color: active ? "white" : "#334155",
            }}
          >
            <Icon className="w-3.5 h-3.5" />
            {t.label}
          </button>
        );
      })}
    </nav>
  );
}

// ─── Tab · Overview ─────────────────────────────────────────

function OverviewTab({ health }) {
  const pillars = [
    { icon: FileCheck,   title: "Trust Request Engine",       body: "Every consumer interaction is a versioned Trust Request — orchestrating KYC, FAIS advice, affordability, suitability across every institution." },
    { icon: ShieldCheck, title: "Altara Trust Utility",        body: "A provider-neutral utility layer that fronts every third-party verification (identity, biometrics, sanctions, credit, geo)." },
    { icon: Award,       title: "Trust Credentials · Passport", body: "Every completed obligation issues a portable Trust Credential. Consumers carry them as a Trust Wallet across institutions — reused, not re-collected." },
    { icon: Activity,    title: "Outcome Governance Engine",   body: "Continuous evaluation of consumer outcomes long after sale: drawdown sustainability, Reg 28, TFSA limits, affordability drift." },
  ];
  return (
    <div className="space-y-6">
      <Card title="What Altara is" icon={Compass}>
        <p className="text-[14px] text-slate-700 leading-relaxed">
          Altara is a <strong>multi-tenant Consumer Trust Infrastructure platform</strong>. It transforms isolated
          compliance activities (KYC, FAIS, TCF, POPIA, Reg 28) into an <strong>orchestration engine</strong> whose
          output is a continuously evolving <strong>Consumer Intelligence Profile</strong> — evidenced, portable,
          and consumer-controlled.
        </p>
        <p className="mt-3 text-[14px] text-slate-700 leading-relaxed">
          Rules decide. AI narrates. Consumers own the record. Institutions plug into the utility.
        </p>
      </Card>

      <Card title="The four pillars" icon={Layers}>
        <div className="grid md:grid-cols-2 gap-4">
          {pillars.map(p => (
            <div key={p.title} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${GOLD}22` }}>
                  <p.icon className="w-4 h-4" style={{ color: NAVY }} />
                </div>
                <div className="font-display text-[15px] font-semibold" style={{ color: NAVY }}>{p.title}</div>
              </div>
              <p className="text-[12.5px] text-slate-600 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Platform engineering directive" icon={Cloud}>
        <p className="text-[14px] text-slate-700 leading-relaxed">
          Every architectural decision assumes <strong>Microsoft Azure</strong> is the production
          platform. Emergent is strictly a development environment. All infrastructure dependencies
          are abstracted behind ports. No new business capabilities are added until the Azure
          platform is production-ready (M1 → M4 complete).
        </p>
        <div className="mt-3 rounded-xl bg-slate-50 border border-slate-200 p-3 text-[12px] font-mono text-slate-700">
          Live env&nbsp;·&nbsp;{health?.env || "—"}&nbsp;&nbsp;·&nbsp;&nbsp;Repository backend&nbsp;·&nbsp;{health?.repository_backend || "—"}
        </div>
      </Card>
    </div>
  );
}

// ─── Tab · Architecture ─────────────────────────────────────

function ArchitectureTab() {
  const stack = [
    { icon: Cpu,      label: "Frontend",       value: "React 19 · TailwindCSS · shadcn/ui · lucide-react" },
    { icon: Server,   label: "Backend",        value: "FastAPI · Python 3.11 · Uvicorn · Pydantic v2" },
    { icon: Database, label: "Datastore",      value: "MongoDB (dev) · SQLite via aiosqlite (dev SQL) · Azure SQL (prod)" },
    { icon: Cpu,      label: "LLM",            value: "Emergent (dev) · Anthropic Claude Sonnet 4.5 (prod)" },
    { icon: KeyRound, label: "Auth",           value: "Emergent-managed Google login · JWT for enterprise" },
    { icon: Cloud,    label: "Deployment",     value: "Docker · GitHub Actions · Azure App Service / Container Apps" },
  ];
  const ports = [
    { name: "RepositoryPort",     status: "live",    body: "Every domain model has an interface in repository_ports.py. Two implementations: Mongo and SQLite/Azure SQL. Selected by REPOSITORY_BACKEND." },
    { name: "LLMPort",             status: "live",    body: "Single Protocol with two adapters: Emergent (dev) and AnthropicDirect (prod). Selected by LLM_PROVIDER. See ADR-0011." },
    { name: "BlobStoragePort",     status: "planned", body: "Filesystem in dev; Azure Blob Storage in prod. Slated for M4." },
    { name: "KeyVaultPort",        status: "planned", body: "Env-only in dev; Azure Key Vault + Managed Identity in prod. M4." },
    { name: "TelemetryPort",       status: "planned", body: "Console/JSON logs in dev; Application Insights in prod. M4." },
  ];
  const adrs = [
    { id: "0001", title: "Azure-first production architecture" },
    { id: "0002", title: "Azure SQL over MongoDB (prod)" },
    { id: "0003", title: "Repository pattern & port abstraction" },
    { id: "0004", title: "Altara Identity — decoupled auth" },
    { id: "0005", title: "Trust Requests — universal orchestration" },
    { id: "0006", title: "Standing Consent — versioned, reusable" },
    { id: "0007", title: "WorkflowRun — first-class entity" },
    { id: "0008", title: "Trust Utility — provider-neutral adapters" },
    { id: "0009", title: "Trust Credentials & Consumer Passport" },
    { id: "0010", title: "Outcome Governance Engine" },
    { id: "0011", title: "LLM abstraction & provider neutrality" },
    { id: "0012", title: "Adopt Navigate Suite deployment framework" },
  ];
  return (
    <div className="space-y-6">
      <Card title="Technology stack" icon={GitBranch}>
        <div className="grid md:grid-cols-2 gap-3">
          {stack.map(s => (
            <div key={s.label} className="flex items-start gap-3 rounded-xl border border-slate-100 bg-white p-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${NAVY}0F` }}>
                <s.icon className="w-4 h-4" style={{ color: NAVY }} />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{s.label}</div>
                <div className="text-[13px] text-slate-800 font-medium">{s.value}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Ports & adapters" icon={Layers}>
        <p className="text-[13px] text-slate-600 leading-relaxed mb-3">
          Every infrastructure dependency lives behind a Protocol. Adapters are swapped by env var —
          no code change between preview and Azure production.
        </p>
        <div className="space-y-2">
          {ports.map(p => (
            <div key={p.name} className="rounded-xl border border-slate-100 bg-white p-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-mono text-[12px] font-semibold" style={{ color: NAVY }}>{p.name}</span>
                <StatusChip status={p.status} />
              </div>
              <p className="text-[12.5px] text-slate-600 leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Architecture Decision Records (ADRs)" icon={BookOpen}>
        <p className="text-[13px] text-slate-600 mb-3 leading-relaxed">
          The 11 ADRs in <code className="text-[11px] bg-slate-100 px-1.5 py-0.5 rounded">docs/adr/</code> capture every non-trivial architectural
          decision, its context, its alternatives, and its consequences.
        </p>
        <div className="grid md:grid-cols-2 gap-2">
          {adrs.map(a => (
            <div key={a.id} className="flex items-center gap-2 text-[12.5px] text-slate-700 rounded-lg border border-slate-100 bg-white px-3 py-2">
              <span className="font-mono text-[11px] text-slate-400 font-semibold">ADR-{a.id}</span>
              <span>{a.title}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ─── Tab · Milestones ───────────────────────────────────────

function MilestonesTab() {
  const milestones = [
    {
      id: "M1", status: "done", date: "Feb 2026",
      title: "Engineering Foundation",
      bullets: [
        "Dockerfiles (Azure Linux base images) for backend & frontend",
        "GitHub Actions CI/CD workflows, CODEOWNERS, PR templates",
        "13 ADRs + 15 infrastructure operations docs",
        "DEVOPS_HANDOVER.md + ENGINEERING_STANDARDS.md",
        "Semantic versioning · v1.0.0",
      ],
    },
    {
      id: "M2", status: "done", date: "Feb 2026",
      title: "LLM Port abstraction",
      bullets: [
        "backend/llm/ — Protocol, factory, and two adapters",
        "EmergentLLMAdapter (dev) · AnthropicDirectAdapter (prod, Claude Sonnet 4.5)",
        "Provider selection via LLM_PROVIDER env var",
        "15 contract tests · 151/151 backend regression tests still passing",
        "ADR-0011 rewritten to match shipped design",
      ],
    },
    {
      id: "M3", status: "todo", date: "Next",
      title: "Router DI refactor",
      bullets: [
        "Migrate remaining ~40 routers off direct db.<collection> calls",
        "Adopt Depends(get_repos) → RepositoryBundle everywhere",
        "Verify REPOSITORY_BACKEND=sql passes contract + e2e end-to-end",
        "~132 call-sites to touch, batched by domain",
      ],
    },
    {
      id: "M4", status: "todo", date: "After M3",
      title: "Azure infrastructure adapters",
      bullets: [
        "BlobStoragePort · Azure Blob Storage adapter (evidence artefacts)",
        "KeyVaultPort · Azure Key Vault + Managed Identity",
        "TelemetryPort · Application Insights adapter",
        "Terraform / Bicep IaC modules under /infra",
      ],
    },
  ];
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-semibold text-amber-900">
          <Milestone className="w-3.5 h-3.5" />
          Engineering freeze in effect
        </div>
        <p className="mt-1 text-[13px] text-amber-900 leading-relaxed">
          No new business capabilities are added to the platform until M3 and M4 are complete.
          This is a deliberate move to guarantee production-readiness on Azure before we widen
          the product surface again.
        </p>
      </div>
      {milestones.map(m => <MilestoneCard key={m.id} m={m} />)}
    </div>
  );
}

function MilestoneCard({ m }) {
  const tone = m.status === "done"
    ? { chip: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2, accent: "#059669" }
    : { chip: "bg-slate-50 text-slate-600 border-slate-200",       icon: Circle,        accent: "#64748B" };
  const Icon = tone.icon;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-5" data-testid={`milestone-${m.id.toLowerCase()}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${tone.accent}18` }}>
          <Icon className="w-5 h-5" style={{ color: tone.accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[11px] font-semibold text-slate-400">{m.id}</span>
            <div className="font-display text-lg font-semibold" style={{ color: NAVY }}>{m.title}</div>
            <span className={`text-[9px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${tone.chip}`}>
              {m.status === "done" ? "shipped" : "planned"}
            </span>
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">{m.date}</div>
        </div>
      </div>
      <ul className="space-y-1.5 pl-1">
        {m.bullets.map((b, i) => (
          <li key={i} className="text-[13px] text-slate-700 leading-relaxed flex gap-2">
            <span className="text-slate-300 select-none">·</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Tab · Features ─────────────────────────────────────────

function FeaturesTab() {
  const groups = [
    {
      title: "Consumer Journeys · Umbrella #1 (everything shipped so far)",
      icon: Compass,
      items: [
        "1.1 Consumer Intelligence — Consumer Register, Journeys, Trust Signals, Timeline",
        "1.2 Institution Workspace — Dashboard, Institutions, Trust Requests, Recommendations, Reports",
        "1.3 Trust Workflows — Consent, Identity, KYC, Affordability, Product Understanding, Learning Requests, Credentials, Templates",
        "1.4 Consumer Education — Learning Hub, Assessments, NAVI, Certificates",
        "1.5 Governance — Trust Evidence, Outcome Governance, Cases, Audit Trail, Reports",
        "1.6 Consumer Trust Wallet — Wallet Home, Trust Wallet, Financial Life, Documents, Institutions, Investments, Life Journeys, Safety, Assistance, Demos",
      ],
    },
    {
      title: "Regulatory Reporting · Umbrella #2 (mini FSCA IRS)",
      icon: FileCheck,
      items: [
        "Reporting Dashboard — 4 status tiles + 12-section OMNI-Risk heatmap + activity feed (LIVE)",
        "OMNI-Risk Return — 12-section workspace scaffold (Phase 1 shipped; field-card UI in Phase 3)",
        "Multi-regulator support — FSCA · FIC · PA · Ombud (tenant flag: TenantRegulatoryConfig)",
        "Regulatory Data Lake, Submission History, XBRL export (Phase 2+)",
      ],
    },
    {
      title: "Platform · Umbrella #3 (admin infrastructure)",
      icon: Layers,
      items: [
        "Tenants · Users · Roles · API Keys — multi-tenant RBAC",
        "Trust Utility — provider-neutral marketplace + per-provider health",
        "Providers · Branding · Feature Flags — extensibility",
        "Audit Logs — every action, tenant-scoped",
        "Platform Health · Platform README (this document)",
      ],
    },
    {
      title: "Underlying platform infrastructure (not menu items)",
      icon: GitBranch,
      items: [
        "RepositoryFactory — Mongo ↔ SQLite/Azure SQL hot-swap via env var",
        "LLMPort — Emergent ↔ Anthropic Claude Sonnet 4.5 hot-swap via env var",
        "Repository contract test suite (78 tests across both backends)",
        "Domain event bus (in-memory today, Service Bus-ready)",
        "Emergent-managed Google Auth + JWT session issuance",
        "Docker images on Azure Linux base",
        "GitHub Actions CI/CD (build · lint · test · docker publish)",
      ],
    },
    {
      title: "Coming next · COFI outcome umbrellas (awaiting menu confirmation)",
      icon: BookOpen,
      items: [
        "Fair Conduct Outcomes — Advice, Product Design & Governance, Marketing & Disclosure, Complaints, Claims, Vulnerable Customers, Post-Sale Barriers",
        "Conduct Risk & Governance — Framework, Register, Root Cause Analysis, Board Packs, Culture, Conduct Officer Console",
        "Financial Crime — AML/CFT, Sanctions, PEP, Fraud, Fit & Proper",
        "Regulatory Reporting — CBR, Ombud, Prudential, Regulatory Correspondence, Attestation Packs",
        "Digital & Cyber Conduct — Data Privacy (POPIA), Cyber Resilience, Algorithmic Accountability, Digital Fraud Signals",
      ],
    },
  ];
  return (
    <div className="space-y-4">
      {groups.map(g => (
        <Card key={g.title} title={g.title} icon={g.icon}>
          <ul className="space-y-2">
            {g.items.map((it, i) => (
              <li key={i} className="text-[13px] text-slate-700 leading-relaxed flex gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: "#059669" }} />
                <span>{it}</span>
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </div>
  );
}

// ─── Tab · Ops ──────────────────────────────────────────────

function OpsTab({ health }) {
  const envKeys = [
    { key: "REPOSITORY_BACKEND", note: "mongo | sql — swap datastore without code changes." },
    { key: "MONGO_URL / DB_NAME", note: "Preview datastore. Not used in Azure production." },
    { key: "SQL_URL",              note: "SQLite (dev SQL) or Azure SQL (production)." },
    { key: "LLM_PROVIDER",         note: "emergent | anthropic — swap LLM provider by env." },
    { key: "EMERGENT_LLM_KEY",     note: "Emergent universal key (dev preview only)." },
    { key: "ANTHROPIC_API_KEY",    note: "Production Claude Sonnet 4.5 key — sourced from Key Vault." },
    { key: "ANTHROPIC_MODEL",      note: "Defaults to claude-sonnet-4-5-20250929." },
    { key: "AZURE_STORAGE_*",      note: "Blob Storage config (M4)." },
    { key: "AZURE_KEY_VAULT_URI",  note: "Key Vault URI for secrets (M4)." },
    { key: "APPLICATIONINSIGHTS_CONNECTION_STRING", note: "Telemetry (M4)." },
  ];
  const cmds = [
    { label: "Run the backend locally",  cmd: "sudo supervisorctl restart backend" },
    { label: "Run backend tests",         cmd: "cd /app/backend && pytest -q" },
    { label: "Run repository contract",   cmd: "cd /app/backend && pytest tests/contracts -q" },
    { label: "Switch to SQL backend",     cmd: "REPOSITORY_BACKEND=sql sudo supervisorctl restart backend" },
    { label: "Switch LLM to Anthropic",   cmd: "LLM_PROVIDER=anthropic sudo supervisorctl restart backend" },
    { label: "Build Docker image",        cmd: "docker build -t altara-backend backend/" },
  ];
  return (
    <div className="space-y-4">
      <Card title="Live platform health" icon={Activity}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <HealthTile label="Status"           value={health?.status || "—"}             good={health?.status === "ok"} />
          <HealthTile label="Version"          value={health?.version || "—"} />
          <HealthTile label="Environment"      value={health?.env || "—"} />
          <HealthTile label="Repo backend"     value={health?.repository_backend || "—"} />
        </div>
      </Card>

      <Card title="Environment variables (key subset)" icon={KeyRound}>
        <p className="text-[13px] text-slate-600 leading-relaxed mb-3">
          See <code className="text-[11px] bg-slate-100 px-1.5 py-0.5 rounded">backend/.env.example</code> for the
          full documented list. All secrets in production are sourced from Azure Key Vault via managed identity.
        </p>
        <div className="rounded-xl border border-slate-100 bg-slate-50 divide-y divide-slate-200">
          {envKeys.map(e => (
            <div key={e.key} className="px-3 py-2 flex items-start gap-4">
              <code className="text-[11.5px] font-mono font-semibold text-slate-800 whitespace-nowrap flex-shrink-0">
                {e.key}
              </code>
              <span className="text-[12.5px] text-slate-600 leading-snug">{e.note}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Common commands" icon={Rocket}>
        <div className="space-y-2">
          {cmds.map(c => (
            <div key={c.label} className="rounded-xl border border-slate-100 bg-white p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{c.label}</div>
              <code className="mt-1 block text-[12px] font-mono text-slate-800 bg-slate-50 rounded-md px-2 py-1.5 overflow-x-auto">
                {c.cmd}
              </code>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Reference documents (in-repo)" icon={BookOpen}>
        <ul className="space-y-1.5">
          {[
            "docs/adr/ — 12 Architecture Decision Records",
            "docs/deployment/NAVIGATE_DEPLOYMENT_FRAMEWORK/ — Azure deploy runbook (adopted verbatim, ADR-0012)",
            "docs/deployment/ALTARA_FRAMEWORK_COMPLIANCE.md — Altara pre-flight gap analysis",
            "docs/infrastructure/ — Azure operations playbooks",
            "docs/AZURE_READINESS_REPORT.md — Production readiness audit",
            "docs/SQL_VALIDATION_REPORT.md — SQL migration validation",
            "DEVOPS_HANDOVER.md · ENGINEERING_STANDARDS.md · memory/PRD.md",
          ].map((t, i) => (
            <li key={i} className="text-[13px] text-slate-700 flex items-center gap-2">
              <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

function HealthTile({ label, value, good }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-white p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        {good !== undefined && (
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: good ? "#10B981" : "#94A3B8" }} />
        )}
        <span className="text-[14px] font-semibold tabular-nums" style={{ color: NAVY }}>{value}</span>
      </div>
    </div>
  );
}

// ─── Shared bits ────────────────────────────────────────────

function Card({ title, icon: Icon, children }) {
  return (
    <section className="rounded-2xl bg-white border border-slate-200 shadow-sm p-5 lg:p-6">
      <header className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${GOLD}22` }}>
          <Icon className="w-4 h-4" style={{ color: NAVY }} />
        </div>
        <h2 className="font-display text-lg font-semibold" style={{ color: NAVY }}>{title}</h2>
      </header>
      {children}
    </section>
  );
}

function StatusChip({ status }) {
  const map = {
    live:    { label: "LIVE",    cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    planned: { label: "PLANNED", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  };
  const s = map[status] || map.planned;
  return (
    <span className={`text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded border ${s.cls}`}>
      {s.label}
    </span>
  );
}
